import {Source, Layer} from '../MaptilerWrapper'
import {useMemo} from 'react'
import PropTypes from 'prop-types'
import {useConfig} from '../../../../../store/hooks/config'
import {generateColorStops, formatPercentage, getMapColors, getPaintProperty} from '../helpers'
import {useTheme} from '@mui/material/styles'
import {useAppSelector} from '../../../../../store/hooks'
import {selectSelectedGradientView, selectSelectedPopulationViewMode} from '../../../../../store/slices/focusLayerSlice'

const CountyTilesLayer = ({
  populationDensityData,
  countyTilesetSourceLayer,
  visibility,
  countyFipsToPersona,
  personasToggle
}) => {
  const theme = useTheme()
  const mapColors = useMemo(() => getMapColors(theme), [theme])
  const {personasConfig} = useConfig()
  const selectedGradientView = useAppSelector(selectSelectedGradientView)
  const selectedPopulationViewMode = useAppSelector(selectSelectedPopulationViewMode)

  // Deduplicated county records (some counties appear multiple times in the API response)
  const filteredData = useMemo(() => {
    if (!populationDensityData?.populationDensity) return null
    const dupFips = new Set(['24510', '29189', '51059', '51067', '51159', '51770'])
    const lastOccurrenceMap = new Map()
    for (let i = populationDensityData.populationDensity.length - 1; i >= 0; i--) {
      const county = populationDensityData.populationDensity[i]
      if (!dupFips.has(county.fips) || !lastOccurrenceMap.has(county.fips)) {
        lastOccurrenceMap.set(county.fips, county)
      }
    }
    return Array.from(lastOccurrenceMap.values())
  }, [populationDensityData])

  // Counts aggregation
  const populationData = useMemo(() => {
    if (!filteredData) return null
    return [
      ...filteredData.map((county) => [county.fips, county.count]),
      ...(populationDensityData.missingGeoIds ?? []).map((fips) => [fips, 0])
    ]
  }, [filteredData, populationDensityData])

  // Mode 1 — selected county / total state population (parent geography)
  const aggForStateTotal = useMemo(() => {
    if (!filteredData) return null
    return [
      ...filteredData.map((county) => [county.fips, formatPercentage(county.statePercentage)]),
      ...(populationDensityData.missingGeoIds ?? []).map((fips) => [fips, 0])
    ]
  }, [filteredData, populationDensityData])

  // Mode 2 — selected county / total county population (same geography)
  const aggForCountyTotal = useMemo(() => {
    if (!filteredData) return null
    return [
      ...filteredData.map((county) => [county.fips, formatPercentage(county.countyPercentage)]),
      ...(populationDensityData.missingGeoIds ?? []).map((fips) => [fips, 0])
    ]
  }, [filteredData, populationDensityData])

  const colors = useMemo(() => {
    if (!populationData) return null
    return generateColorStops(populationData, mapColors, false)
  }, [populationData, mapColors])

  const colorsForStateTotal = useMemo(() => {
    if (!aggForStateTotal?.length) return null
    return generateColorStops(aggForStateTotal, mapColors, true)
  }, [aggForStateTotal, mapColors])

  const colorsForCountyTotal = useMemo(() => {
    if (!aggForCountyTotal?.length) return null
    return generateColorStops(aggForCountyTotal, mapColors, true)
  }, [aggForCountyTotal, mapColors])

  const layerType = personasToggle ? 'personas' : 'populationDensity'

  const colorsMap = (() => {
    if (selectedGradientView === 'population') return colors
    if (selectedPopulationViewMode === 'selected_of_state_total') return colorsForStateTotal
    return colorsForCountyTotal
  })()

  const aggregationMap = (() => {
    if (selectedGradientView === 'population') return populationData
    if (selectedPopulationViewMode === 'selected_of_state_total') return aggForStateTotal
    return aggForCountyTotal
  })()
  return (
    populationData &&
    colors && (
      <Source
        id='counties'
        type='vector'
        url={'https://api.maptiler.com/tiles/0195b7e8-b1a6-769c-aedb-7cc856b7403a/tiles.json'}
        promoteId='GEOID'
      >
        <Layer
          beforeId='state-border'
          id='counties-border'
          type='line'
          source='counties'
          source-layer={countyTilesetSourceLayer}
          paint={{
            'line-color': theme.palette.hpWhite,
            'line-width': 0.5,
            'line-opacity': 1
          }}
        />
        {aggregationMap && colorsMap && (
          <Layer
            key='counties-fill'
            beforeId='counties-border'
            id='counties-fill'
            type='fill'
            source='counties'
            source-layer={countyTilesetSourceLayer}
            filter={['in', ['get', 'GEOID'], ['literal', aggregationMap.map((d) => d[0])]]}
            paint={getPaintProperty(layerType, countyFipsToPersona, personasConfig, aggregationMap, colorsMap)}
            layout={{visibility}}
            data-testid='counties-fill'
          />
        )}
        <Layer
          id='counties-border-onhover'
          type='line'
          source='counties'
          source-layer={countyTilesetSourceLayer}
          paint={{
            'line-color': theme.palette.hpAccentBlack,
            'line-width': 2,
            'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0]
          }}
        />
        <Layer
          id='counties-border-onhover-stroke'
          type='line'
          source='counties'
          source-layer={countyTilesetSourceLayer}
          paint={{
            'line-color': theme.palette.hpWhite,
            'line-width': 2,
            'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0],
            'line-gap-width': 2
          }}
        />
      </Source>
    )
  )
}

CountyTilesLayer.propTypes = {
  populationDensityData: PropTypes.object,
  countyTilesetSourceLayer: PropTypes.string,
  visibility: PropTypes.string,
  countyFipsToPersona: PropTypes.object,
  personasToggle: PropTypes.bool
}

export default CountyTilesLayer

/**
 * List of functions and their purpose:
 *
 * - getPaintProperty: Determines the paint properties for the map layer based on personas or population density data.
 * - CountyTilesLayer: Renders county tiles on the map with dynamic fill colors, borders, and hover effects.
 */
